// src/components/layout/Header.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { scrollToTop } from '../../utils/scrollUtils';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
    scrollToTop();
  };
  
  // Check if current path matches the given path
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white bg-opacity-95 shadow-md py-2' 
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container-wide flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center" onClick={closeMenu}>
          <img 
            src={isScrolled 
              ? "/assets/images/oljarna-krajnc-fram-logo-transparent-64.png" 
              : "/assets/images/oljarna-krajnc-fram-logo-transparent-64.png"} 
            alt="Oljarna Krajnc Fram"
            width={64}
            height={64}
            className="h-12 md:h-14 w-auto" // Adjusted height for better alignment
          />
          <div className="ml-2 flex flex-col justify-center"> {/* Adjusted margin and flex for centering */}
            <h1 className="text-primary text-xl md:text-2xl font-bold">
              OLJARNA KRAJNC
            </h1>
            <p className="text-xs md:text-sm text-accent-700 hidden sm:block">
              Tradicija od leta 1927
            </p>
          </div>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-8">
          <NavLink to="/" label="Domov" isActive={isActive('/')} />
          <NavLink to="/o-nas" label="O nas" isActive={isActive('/o-nas')} />
          <NavLink to="/izdelki" label="Izdelki" isActive={isActive('/izdelki')} />
          <NavLink to="/kontakt" label="Kontakt" isActive={isActive('/kontakt')} />
          <a 
            href="tel:+38631654929" 
            className="btn-primary flex items-center px-4 py-2 group transition-all duration-300 hover:bg-primary-800 hover:shadow-[0_0_15px_rgba(42,93,60,0.5)]" // Added padding for consistent height
          >
            <Phone size={18} className="mr-2" />
            Naročite zdaj
          </a>
        </nav>
        
        {/* Mobile CTA and Menu Button */}
        <div className="flex items-center space-x-2 lg:hidden">
          {/* Mobile Call Button */}
          <a 
            href="tel:+38631654929"
            className="bg-primary w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-primary-800 hover:shadow-[0_0_10px_rgba(42,93,60,0.5)] transition-all duration-300"
            aria-label="Naročite zdaj"
          >
            <Phone size={20} />
          </a>
          
          {/* Mobile Menu Button */}
          <button 
            className="text-primary focus:outline-none bg-gray-100 hover:bg-gray-200 p-2 rounded-md transition-colors" 
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Zapri meni' : 'Odpri meni'}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      <div 
        className={`lg:hidden fixed inset-0 bg-white z-40 transition-transform duration-300 ease-in-out transform ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        } pt-24`}
      >
        <div className="absolute top-4 right-4">
          <button 
            className="w-10 h-10 flex items-center justify-center text-primary bg-gray-800 hover:bg-gray-900 rounded-md transition-colors focus:outline-none" 
            onClick={toggleMenu}
            aria-label="Zapri meni"
          >
            <X size={24} className="text-white" />
          </button>
        </div>
        
        <nav className="container flex flex-col space-y-4 p-6">
          <MobileNavLink to="/" label="Domov" isActive={isActive('/')} onClick={closeMenu} />
          <MobileNavLink to="/o-nas" label="O nas" isActive={isActive('/o-nas')} onClick={closeMenu} />
          <MobileNavLink to="/izdelki" label="Izdelki" isActive={isActive('/izdelki')} onClick={closeMenu} />
          <MobileNavLink to="/kontakt" label="Kontakt" isActive={isActive('/kontakt')} onClick={closeMenu} />
          <div className="pt-4">
            <a 
              href="tel:+38631654929"
              className="btn-primary flex items-center justify-center w-full group transition-all duration-300 hover:bg-primary-800 hover:shadow-[0_0_15px_rgba(42,93,60,0.5)]"
            >
              <Phone size={18} className="mr-2" />
              Naročite zdaj
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

// Desktop navigation link component
const NavLink: React.FC<{ 
  to: string; 
  label: string; 
  isActive: boolean;
}> = ({ to, label, isActive }) => {
  return (
    <Link 
      to={to} 
      className={`text-base font-medium relative transition-colors hover:text-primary-800 group flex items-center h-10 ${
        isActive 
          ? 'text-primary-700 font-semibold' 
          : 'text-primary-600'
      }`} // Added flex and height for alignment
      onClick={scrollToTop}
    >
      {label}
      <span className={`absolute -bottom-2 left-0 right-0 h-0.5 bg-secondary transform origin-left transition-transform duration-300 ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
    </Link>
  );
};

// Mobile navigation link component
const MobileNavLink: React.FC<{ 
  to: string; 
  label: string; 
  isActive: boolean; 
  onClick: () => void;
}> = ({ to, label, isActive, onClick }) => {
  return (
    <Link 
      to={to} 
      className={`text-xl py-3 px-4 border-b border-gray-100 transition-colors hover:bg-gray-50 hover:text-primary-800 ${
        isActive 
          ? 'text-primary-700 font-semibold' 
          : 'text-primary-600'
      }`}
      onClick={onClick}
    >
      {label}
    </Link>
  );
};

export default Header;